import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { getMe, cancelSubscription, deleteAccount } from "../services/account";
import { useLocation } from "react-router-dom";

export default function ManageAccount() {
  const { logout } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState<"active" | "canceling" | "free">("free");
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState(""); 
  const [cancelError, setCancelError] = useState("");   
  
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const location = useLocation();

  const loadUser = async () => {
    try {
      const res = await getMe();
      setEmail(res.data.email);
      setPlan(res.data.plan);
      setStatus(res.data.stripeSubscriptionStatus || "free");
      setPeriodEnd(res.data.currentPeriodEnd);
    } catch (err) {
      console.error("Failed to load user data", err);
    }
  };

  useEffect(() => {
    loadUser();
    const params = new URLSearchParams(location.search);
    if (params.get("success") === "true") {
      loadUser();
    }
  }, [location.search]);

  const confirmCancel = async () => {
    // Double-check validation on submit
    if (cancelReason.trim().length < 10) {
      setCancelError("Please provide a brief reason (minimum 10 characters).");
      return;
    }
    
    setCancelError("");
    
    try {
      await cancelSubscription({ reason: cancelReason });
      setShowConfirmCancel(false);
      setShowCancelSuccess(true);
      setCancelReason(""); // Reset form field
      await loadUser();
    } catch (err) {
      setCancelError("Failed to cancel subscription. Please try again.");
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteAccount();
      setShowConfirmDelete(false);
      setShowDeleteSuccess(true);
      await logout();
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      alert("Failed to delete account. Please try again.");
      setShowConfirmDelete(false);
    }
  };

  const formatDate = (date: string | null) =>
    date ? new Date(date).toLocaleDateString() : "";

  // The condition that determines if the user is allowed to proceed
  const isFormInvalid = cancelReason.trim().length < 10;

  return (
    <div className="page-shell px-4 py-12">
      <div className="mx-auto max-w-xl">
        <h2 className="mb-8 text-3xl font-extrabold text-secondary">Manage Account</h2>

        <div className="ui-card space-y-6 p-6">
          <div className="space-y-1">
            <p className="text-sm text-textMuted">Email</p>
            <p className="text-lg font-semibold text-secondary">{email}</p>
          </div>

          <div className="space-y-1">
            <p className="text-sm text-textMuted">Plan</p>
            <p className="text-lg font-semibold capitalize text-secondary">
              {plan}
              {periodEnd && status === "active" && (
                <span className="mt-1 block text-sm font-medium text-success">
                  Renews on {formatDate(periodEnd)}
                </span>
              )}
              {periodEnd && status === "canceling" && (
                <span className="mt-1 block text-sm font-medium text-warning">
                  Canceled — expires on {formatDate(periodEnd)}
                </span>
              )}
            </p>
          </div>

          <button
            onClick={() => (window.location.href = "/forgot-password")}
            className="btn-secondary w-full"
          >
            Reset Password
          </button>

          {status === "active" && plan !== "free" && (
            <button
              onClick={() => setShowConfirmCancel(true)}
              className="btn-warning w-full"
            >
              Cancel Subscription
            </button>
          )}

          {status === "canceling" && (
            <button
              onClick={() => (window.location.href = "/pricing")}
              className="btn-success w-full"
            >
              Renew Subscription
            </button>
          )}

          <button
            onClick={() => setShowConfirmDelete(true)}
            className="btn-danger w-full"
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Cancel Subscription Modal - MANDATORY REASON FLOW */}
      {showConfirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="ui-card w-full max-w-md space-y-5 p-8">
            <div className="text-center space-y-2">
              <h3 className="text-xl font-bold text-secondary">Cancel Subscription</h3>
              <p className="text-sm text-textMuted">
                Are you sure you want to cancel? We hate to see you go! To proceed, please let us know what we can improve.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-textMuted">
                Why are you leaving? <span className="text-red-600">* Required</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => {
                  setCancelReason(e.target.value);
                  if (e.target.value.trim().length >= 10) setCancelError("");
                }}
                placeholder="Please write at least 10 characters..."
                className="ui-textarea h-28 text-sm"
              />
              {cancelError && (
                <p className="text-center text-xs text-red-600">{cancelError}</p>
              )}
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <button
                onClick={confirmCancel}
                disabled={isFormInvalid}
                className={`w-full ${
                  isFormInvalid
                    ? "cursor-not-allowed rounded-xl bg-slate-300 px-5 py-3 font-semibold text-slate-500"
                    : "btn-warning"
                }`}
              >
                Yes, Cancel Subscription
              </button>
              
              <button
                onClick={() => {
                  setShowConfirmCancel(false);
                  setCancelReason("");
                  setCancelError("");
                }}
                className="btn-secondary w-full text-sm"
              >
                No, Keep My Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showConfirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="ui-card w-full max-w-md space-y-6 p-8 text-center">
            <p className="text-xl font-bold text-red-600">
              WARNING: This will permanently delete your account and all data.
            </p>
            <p className="text-lg text-textMuted">Are you absolutely sure?</p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                onClick={confirmDelete}
                className="btn-danger"
              >
                Yes, delete my account
              </button>
              <button
                onClick={() => setShowConfirmDelete(false)}
                className="btn-secondary"
              >
                No, keep my account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Success */}
      {showCancelSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="ui-card space-y-4 p-8 text-center">
            <p className="text-xl font-medium text-success">
              Subscription canceled successfully.
            </p>
            <button
              onClick={() => setShowCancelSuccess(false)}
              className="btn-primary"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Success */}
      {showDeleteSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="ui-card space-y-4 p-8 text-center">
            <p className="text-xl font-bold text-red-600">
              Your account has been deleted.
            </p>
            <p className="text-textMuted">Redirecting you to the homepage...</p>
          </div>
        </div>
      )}
    </div>
  );
}